(() => {
  const ACCORDION_SELECTOR = [
    '.accordion > details',
    '.halden-faq__item',
    '.halden-product-accordion',
    '.promo-accordion',
    '.disclosures__details',
    '#Details-CartDrawer'
  ].join(',');

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class HaldenSmoothDetails {
    constructor(details) {
      if (!details || details.dataset.haldenSmoothAccordion === 'true') return;

      const summary = details.querySelector(':scope > summary');
      if (!summary) return;

      this.details = details;
      this.summary = summary;
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.anchorFrame = null;
      this.anchorTop = null;
      this.shouldLockAnchor = details.matches('.halden-product-accordion');
      this.onClick = this.onClick.bind(this);

      details.dataset.haldenSmoothAccordion = 'true';
      summary.addEventListener('click', this.onClick);
    }

    onClick(event) {
      if (event.defaultPrevented) return;

      event.preventDefault();

      if (prefersReducedMotion() || typeof this.details.animate !== 'function') {
        this.cancelAnimation();
        this.details.open = !this.details.open;
        return;
      }

      if (this.isClosing || !this.details.open) {
        this.open();
      } else if (this.isExpanding || this.details.open) {
        this.close();
      }
    }

    cancelAnimation() {
      if (this.animation) {
        this.animation.cancel();
        this.animation = null;
      }
      this.stopAnchorLock();
      this.isClosing = false;
      this.isExpanding = false;
    }

    startAnchorLock() {
      if (!this.shouldLockAnchor) return;

      this.stopAnchorLock();
      this.anchorTop = this.summary.getBoundingClientRect().top;

      const keepSummaryFixed = () => {
        if (this.anchorTop === null) return;

        const currentTop = this.summary.getBoundingClientRect().top;
        const drift = currentTop - this.anchorTop;

        if (Math.abs(drift) > 0.5) {
          window.scrollBy(0, drift);
        }

        this.anchorFrame = requestAnimationFrame(keepSummaryFixed);
      };

      this.anchorFrame = requestAnimationFrame(keepSummaryFixed);
    }

    stopAnchorLock() {
      if (this.anchorFrame !== null) {
        cancelAnimationFrame(this.anchorFrame);
        this.anchorFrame = null;
      }
      this.anchorTop = null;
    }

    close() {
      this.cancelAnimation();

      const startHeight = `${this.details.offsetHeight}px`;
      const endHeight = `${this.summary.offsetHeight}px`;

      this.isClosing = true;
      this.details.style.overflow = 'hidden';
      this.startAnchorLock();

      this.animation = this.details.animate(
        {
          height: [startHeight, endHeight]
        },
        {
          duration: 300,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
        }
      );

      this.animation.onfinish = () => {
        this.details.open = false;
        this.finishAnimation();
      };

      this.animation.oncancel = () => {
        this.isClosing = false;
      };
    }

    open() {
      this.cancelAnimation();

      // Lock the collapsed height before opening. This prevents the browser
      // from laying out the full content first (which can make the accordion
      // appear to expand upward because of scroll anchoring).
      const startHeightValue = this.summary.offsetHeight;
      const startHeight = `${startHeightValue}px`;

      this.details.style.height = startHeight;
      this.details.style.overflow = 'hidden';
      this.details.open = true;

      // scrollHeight gives us the complete open height while the visible box
      // remains locked at the summary height, so the reveal grows downward.
      const endHeight = `${this.details.scrollHeight}px`;
      const content = Array.from(this.details.children).filter((child) => child !== this.summary);

      this.isExpanding = true;
      this.startAnchorLock();

      requestAnimationFrame(() => {
        this.animation = this.details.animate(
          {
            height: [startHeight, endHeight]
          },
          {
            duration: 300,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
          }
        );

        content.forEach((element) => {
          element.animate(
            [
              { opacity: 0, transform: 'translateY(-6px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ],
            {
              duration: 240,
              delay: 35,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              fill: 'both'
            }
          );
        });

        this.animation.onfinish = () => this.finishAnimation();
        this.animation.oncancel = () => {
          this.isExpanding = false;
        };
      });
    }

    finishAnimation() {
      this.animation = null;
      this.isClosing = false;
      this.isExpanding = false;
      this.stopAnchorLock();
      this.details.style.removeProperty('height');
      this.details.style.removeProperty('overflow');
    }
  }

  const initAccordions = (root = document) => {
    const elements = [];

    if (root instanceof Element && root.matches(ACCORDION_SELECTOR)) {
      elements.push(root);
    }

    if (root.querySelectorAll) {
      elements.push(...root.querySelectorAll(ACCORDION_SELECTOR));
    }

    elements.forEach((details) => new HaldenSmoothDetails(details));
  };

  const init = () => initAccordions(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => initAccordions(event.target));
  document.addEventListener('shopify:block:select', (event) => initAccordions(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof Element) initAccordions(node);
      });
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
